import type { AttachmentFile } from './types'

/**
 * Encodes the filename part of a URL path, preserving the directory structure.
 * Splits at the last '/' and only encodes the filename portion.
 *
 * Currently duplicated in: main Uploader, SubInfo, Files, FileViewer.
 */
export function encodeFilePathForUrl(
  path: string,
  fallback?: string
): string {
  if (typeof path === 'string') {
    const lastIndex = path.lastIndexOf('/')
    return (
      path.substring(0, lastIndex + 1) +
      encodeURIComponent(path.substring(lastIndex + 1))
    )
  }
  return fallback ?? path
}

const IMAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/gif',
  'image/svg+xml',
  'image/webp',
  'image/*',
])

const IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  '.jpg',
  '.jpeg',
  '.png',
  '.bmp',
  '.gif',
  '.svg',
  '.webp',
])

const PDF_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
])

const PDF_EXTENSIONS: ReadonlySet<string> = new Set([
  '.pdf',
])

const WORD_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

const WORD_EXTENSIONS: ReadonlySet<string> = new Set([
  '.doc',
  '.docx',
])

const EXCEL_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

const EXCEL_EXTENSIONS: ReadonlySet<string> = new Set([
  '.xls',
  '.xlsx',
])

/**
 * Normalizes an extension string to lowercase for comparison.
 * Returns an empty string when the input is nullish or not a string.
 */
function normalize(ext: string | undefined | null): string {
  if (typeof ext !== 'string') return ''
  return ext.trim().toLowerCase()
}

/**
 * Checks whether a value matches any of the given MIME types or file extensions.
 */
function matchesMimeOrExtension(
  value: string,
  mimeTypes: ReadonlySet<string>,
  extensions: ReadonlySet<string>
): boolean {
  if (mimeTypes.has(value)) return true

  let found = false
  extensions.forEach((ext) => {
    if (value.endsWith(ext)) found = true
  })
  if (found) return true

  return false
}

/**
 * Check if a MIME type or file extension represents an image.
 *
 * Replaces 40+ lines of filtering in SubInfo and similar checks in ImageGroupViewer.
 * Supports both full MIME types (e.g. 'image/jpeg') and file extensions (e.g. '.jpg').
 * Also matches values that start with 'image/' for generic image MIME types.
 */
export function isImageExtension(ext?: string): boolean {
  const value = normalize(ext)
  if (!value) return false

  if (value.startsWith('image/')) return true

  return matchesMimeOrExtension(value, IMAGE_MIME_TYPES, IMAGE_EXTENSIONS)
}

/**
 * Check if a MIME type or file extension is a PDF.
 */
export function isPdfExtension(ext?: string): boolean {
  const value = normalize(ext)
  if (!value) return false

  return matchesMimeOrExtension(value, PDF_MIME_TYPES, PDF_EXTENSIONS)
}

/**
 * Check if a MIME type or file extension is a Word document.
 */
export function isWordExtension(ext?: string): boolean {
  const value = normalize(ext)
  if (!value) return false

  return matchesMimeOrExtension(value, WORD_MIME_TYPES, WORD_EXTENSIONS)
}

/**
 * Check if a MIME type or file extension is an Excel spreadsheet.
 */
export function isExcelExtension(ext?: string): boolean {
  const value = normalize(ext)
  if (!value) return false

  return matchesMimeOrExtension(value, EXCEL_MIME_TYPES, EXCEL_EXTENSIONS)
}

/**
 * Check if a MIME type or file extension is a document (PDF, Word, or Excel).
 */
export function isDocumentExtension(ext?: string): boolean {
  return isPdfExtension(ext) || isWordExtension(ext) || isExcelExtension(ext)
}

/**
 * Get the effective extension from an AttachmentFile.
 * Checks `uploadedFile?.extension` first, then falls back to `extension`.
 * Returns an empty string when neither is available.
 */
export function getFileExtension(file: AttachmentFile): string {
  return file.uploadedFile?.extension ?? file.extension ?? ''
}
